import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { rtkApi } from '../../../RTK/api/rtkApi';
import {
  DASHBOARD_SYSTEM_FOLDERS,
  removeChildActionItems,
  setChildActionItems,
} from '../../utils/app-constants';
import { getLastMonthData, getLastWeekData } from '../../utils/helpers/generateDashboardFilters';
import { getActionTileData } from '../../utils/helpers/getActionTileData';
import {
  ICustomFilterQuery,
  IDashboardStateAction,
  IDashboardSummary,
  IState,
  ITime,
  IWorkItem,
} from '../typings';
import { IChartInfoResponse } from 'src/Common/RTK/interface/IChartInfoResponse';

interface IDashboardSummaryState {
  dashboardActions: IDashboardSummary;
  actionItems: IWorkItem[];
  customFilters: ICustomFilterQuery[];
  view: ICustomFilterQuery;
  actionsCount: number;
  actionSummaryMap: IDashboardStateAction[];
  statusMap: IChartInfoResponse;
  filter: {
    state: IState;
    time: ITime;
  };
  viewId: string;
}

const initialState: IDashboardSummaryState = {
  dashboardActions: {
    actions: [],
    actionsCount: 0,
    queryId: '',
  },
  customFilters: [],
  actionItems: [],
  actionsCount: 0,
  actionSummaryMap: [],
  statusMap: {
    pieChartInfo: {
      stateInfo: [],
    },
    stackChartInfo: {
      actions: [],
    },
  },
  filter: {
    state: 'All',
    time: 'All',
  },
  view: {} as ICustomFilterQuery,
  viewId: '',
};

export const dashboardSummarySlice = createSlice({
  name: 'dashboardSummary',
  initialState,
  reducers: {
    setDashboardActionFilterBasedOnState(state, action: PayloadAction<IState>) {
      switch (action.payload.toLowerCase().replace(/\s/g, '')) {
        case 'all':
          if (state.filter.time === 'Last Week') {
            state.actionItems = getLastWeekData(state.dashboardActions.actions);
          } else if (state.filter.time === 'Last Month') {
            state.actionItems = getLastMonthData(state.dashboardActions.actions);
          } else if (state.filter.time === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else {
            state.actionItems = state.dashboardActions.actions;
          }

          break;
        case 'done':
          if (state.filter.time === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else if (state.filter.time === 'Last Week') {
            state.actionItems = getLastWeekData(state.dashboardActions.actions);
          } else if (state.filter.time === 'Last Month') {
            state.actionItems = getLastMonthData(state.dashboardActions.actions);
          }
          state.actionItems = state.actionItems.filter(
            (item) => item.Fields['System.State'] === 'Done'
          );
          break;
        case 'inprogress':
          if (state.filter.time === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else if (state.filter.time === 'Last Week') {
            state.actionItems = getLastWeekData(state.dashboardActions.actions);
          } else if (state.filter.time === 'Last Month') {
            state.actionItems = getLastMonthData(state.dashboardActions.actions);
          }
          state.actionItems = state.actionItems.filter(
            (item) => item.Fields['System.State'] === 'In Progress'
          );
          break;
        case 'onhold':
          if (state.filter.time === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else if (state.filter.time === 'Last Week') {
            state.actionItems = getLastWeekData(state.dashboardActions.actions);
          } else if (state.filter.time === 'Last Month') {
            state.actionItems = getLastMonthData(state.dashboardActions.actions);
          }
          state.actionItems = state.actionItems.filter(
            (item) => item.Fields['System.State'] === 'On Hold'
          );
          break;
        case 'new':
          if (state.filter.time === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else if (state.filter.time === 'Last Week') {
            state.actionItems = getLastWeekData(state.dashboardActions.actions);
          } else if (state.filter.time === 'Last Month') {
            state.actionItems = getLastMonthData(state.dashboardActions.actions);
          }
          state.actionItems = state.actionItems.filter(
            (item) => item.Fields['System.State'] === 'New'
          );
          break;
        default:
          state.actionItems = state.dashboardActions.actions.filter(
            (item) => item.Fields['System.State'] === action.payload
          );
      }
    },
    setDashboardActionFilterBasedOnTime(state, action: PayloadAction<ITime>) {
      state.filter.time = action.payload;
      switch (action.payload.toLowerCase().replace(/\s/g, '')) {
        case 'all':
          if (state.filter.state !== 'All') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === state.filter.state
            );
          } else {
            state.actionItems = state.dashboardActions.actions;
          }
          break;

        case 'lastweek':
          const lastWeekStart = moment().subtract(1, 'week').startOf('week');
          const lastWeekEnd = moment().subtract(1, 'week').endOf('week');
          if (state.filter.state === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else if (state.filter.state === 'Done') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'Done'
            );
          } else if (state.filter.state === 'In Progress') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'In Progress'
            );
          } else if (state.filter.state === 'On Hold') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'On Hold'
            );
          } else if (state.filter.state === 'New') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'New'
            );
          }
          state.actionItems = state.actionItems.filter((item) => {
            const itemDate = moment(item.Fields['System.ChangedDate']);
            return itemDate.isBetween(lastWeekStart, lastWeekEnd, null, '[]'); // '[]' includes both ends
          });
          break;
        case 'lastmonth':
          const lastMonthStart = moment().subtract(1, 'month').startOf('month');
          const lastMonthEnd = moment().subtract(1, 'month').endOf('month');
          if (state.filter.state === 'All') {
            state.actionItems = state.dashboardActions.actions;
          } else if (state.filter.state === 'Done') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'Done'
            );
          } else if (state.filter.state === 'In Progress') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'In Progress'
            );
          } else if (state.filter.state === 'On Hold') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'On Hold'
            );
          } else if (state.filter.state === 'New') {
            state.actionItems = state.dashboardActions.actions.filter(
              (item) => item.Fields['System.State'] === 'New'
            );
          }
          state.actionItems = state.actionItems.filter((item) => {
            const itemDate = moment(item.Fields['System.ChangedDate']);
            return itemDate.isBetween(lastMonthStart, lastMonthEnd, null, '[]'); // '[]' includes both ends
          });
          break;
        default:
          state.actionItems = state.dashboardActions.actions.filter(
            (item) => item.Fields['System.ChangedDate'] === action.payload
          );
      }
    },
    setViews(state, action: PayloadAction<string>) {
      switch (action.payload.toLowerCase()) {
        case DASHBOARD_SYSTEM_FOLDERS.AssignedToMe:
          state.view = state.customFilters.find(
            (item) => item.name.toLowerCase() === DASHBOARD_SYSTEM_FOLDERS.AssignedToMe
          ) as ICustomFilterQuery;
          break;
        case DASHBOARD_SYSTEM_FOLDERS.RequestedByMe:
          state.view = state.customFilters.find(
            (item) => item.name.toLowerCase() === DASHBOARD_SYSTEM_FOLDERS.RequestedByMe
          ) as ICustomFilterQuery;
          break;
        case DASHBOARD_SYSTEM_FOLDERS.FollowedByMe:
          state.view = state.customFilters.find(
            (item) => item.name.toLowerCase() === DASHBOARD_SYSTEM_FOLDERS.FollowedByMe
          ) as ICustomFilterQuery;
          break;
        case DASHBOARD_SYSTEM_FOLDERS.AllActions:
          state.view = state.customFilters.find(
            (item) => item.name.toLowerCase() === 'all actions'
          ) as ICustomFilterQuery;
          break;
        default:
          state.view = state.customFilters.find(
            (item) => item.name.toLowerCase() === DASHBOARD_SYSTEM_FOLDERS.AssignedToMe
          ) as ICustomFilterQuery;
          break;
      }
    },
    setViewId(state, action: PayloadAction<string>) {
      state.viewId = action.payload;
    },
    setActionsCount(state, action: PayloadAction<number>) {
      state.actionsCount = action.payload;
    },
    clearActionItems(state) {
      state.actionItems = [];
    },
  },
  extraReducers(builder) {
    builder.addCase(setChildActionItems, (state, action) => {
      const index = state.actionItems.findIndex((item) => item.Id === action.payload.id);
      if (index !== -1) {
        state.actionItems[index]['RelationsItems'] = action.payload.childActions;
        action.payload.childActions.forEach((childAction) => {
          state.actionItems.splice(index + 1, 0, childAction);
        });
      }
    });

    builder.addCase(removeChildActionItems, (state, action) => {
      state.actionItems = state.actionItems.filter((item) => {
        return (
          (action.payload['primaryLinkText'] &&
            item?.parentId !== action.payload['primaryLinkText']) ||
          (action.payload['Id'] && item?.parentId !== action.payload['Id'])
        );
      });
      state.actionItems = state.actionItems.map((item) => {
        if (
          (action.payload['primaryLinkText'] && item?.Id == action.payload['primaryLinkText']) ||
          (action.payload['Id'] && item?.Id == action.payload['Id'])
        ) {
          item['RelationsItems'] = [];
        }
        return item;
      });
    });

    builder.addMatcher(rtkApi.endpoints.GetDashboard.matchFulfilled, (state, action) => {
      state.dashboardActions = action.payload;
      const actionTileData = getActionTileData(action.payload.actions);
      const viewName = state.customFilters.find((filter) => filter.id === action.payload.queryId);
      if (!viewName) return;
      state.actionSummaryMap.push({
        key: viewName.name,
        value: actionTileData,
      });

      let actions: IWorkItem[];
      if (state.filter.state !== 'All') {
        actions = action.payload.actions.filter(
          (item) => item.Fields['System.State'] === state.filter.state
        );
      } else {
        actions = action.payload.actions;
      }

      if (state.filter.time !== 'All') {
        switch (state.filter.time) {
          case 'Last Week':
            actions = getLastWeekData(actions);
            break;
          case 'Last Month':
            actions = getLastMonthData(actions);
            break;
          default:
            actions = actions;
        }
      }
      state.actionItems = actions;
    });
    builder.addMatcher(rtkApi.endpoints.GetViewById.matchFulfilled, (state, action) => {
      state.view = action.payload;
      state.customFilters = [...state.customFilters, action.payload];
    });
    builder.addMatcher(rtkApi.endpoints.GetChartInfo.matchFulfilled, (state, action) => {
      state.statusMap = action.payload;
    });
    builder.addMatcher(rtkApi.endpoints.GetViews.matchFulfilled, (state, action) => {
      state.customFilters = action.payload.customFilters;
      state.view = action.payload.customFilters.find(
        (item) => item.name.toLowerCase() === DASHBOARD_SYSTEM_FOLDERS.AssignedToMe
      ) as ICustomFilterQuery;
    });
    builder.addMatcher(rtkApi.endpoints.GetActionCount.matchFulfilled, (state, action) => {
      state.actionsCount = action.payload.actionsCount;
    });
  },
});

export const {
  setDashboardActionFilterBasedOnState,
  setDashboardActionFilterBasedOnTime,
  setActionsCount,
  setViews,
  setViewId,
  clearActionItems,
} = dashboardSummarySlice.actions;
